import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowRightLeft, Upload, CheckCircle2, ShieldCheck,
  Users, FileText, Package, RotateCcw, Sparkles,
} from 'lucide-react';

/**
 * Wiederverwendbare Marketing-Sektion „Umzugs-Assistent / Zero Switching Costs".
 * Wird auf der Landingpage und den Alternativen-Seiten eingebunden.
 *
 * Props:
 *  - highlightTool: optionaler Quell-Tool-Name (z.B. "sevDesk") für eine zugespitzte Headline.
 *  - ctaTo: Ziel des CTA-Buttons (default "/signup").
 *  - onCtaClick: optionaler Tracking-Callback.
 *  - innerRef: optionale Section-Ref (z.B. für Scroll-Tracking).
 */
export default function MigrationSwitchSection({
  highlightTool = null,
  ctaTo = '/signup',
  onCtaClick,
  innerRef,
}) {
  const sources = ['sevDesk', 'Lexware Office', 'DATEV (EXTF)', 'CSV / Excel'];

  const steps = [
    {
      icon: ArrowRightLeft,
      title: '1. Quell-Tool & Export wählen',
      text: highlightTool
        ? `Exportiere deine Daten aus ${highlightTool} (Kunden, Artikel, Buchungen) — gängige Formate werden direkt erkannt.`
        : 'Exportiere deine Daten aus dem bisherigen Tool — sevDesk, Lexware Office, DATEV oder eine generische CSV.',
    },
    {
      icon: Upload,
      title: '2. Hochladen — KI erkennt die Spalten',
      text: 'Datei hochladen, die intelligente Spaltenerkennung ordnet die Felder automatisch zu. Kein manuelles Mapping nötig.',
    },
    {
      icon: CheckCircle2,
      title: '3. Vorschau prüfen & importieren',
      text: 'Alles vor dem Import in der Vorschau kontrollieren, dann übernehmen. Nicht zufrieden? Mit einem Klick zurückrollen.',
    },
  ];

  const carried = [
    { icon: Users, label: 'Kunden & Kontakte' },
    { icon: Package, label: 'Standardartikel & Leistungen' },
    { icon: FileText, label: 'Rechnungen, Buchungen & Belege' },
  ];

  return (
    <section
      ref={innerRef}
      className="py-16 bg-gradient-to-b from-white to-emerald-50"
      data-testid="migration-switch-section"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 text-emerald-800 text-sm font-semibold px-4 py-1.5 mb-4">
            <Sparkles className="h-4 w-4" /> Zero Switching Costs
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            {highlightTool
              ? `Von ${highlightTool} zu ivoliq – ohne Datenverlust`
              : 'Wechseln ohne Wechselkosten'}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Der <strong>Umzugs-Assistent</strong> holt deine bestehenden Daten geführt und{' '}
            <strong>GoBD-konform</strong> zu ivoliq — meist in wenigen Minuten. Kein Copy-&-Paste,
            kein Datenverlust, kein Risiko.
          </p>
        </div>

        {/* 3 Schritte */}
        <div className="grid md:grid-cols-3 gap-5 mb-10">
          {steps.map((s) => (
            <div
              key={s.title}
              className="bg-white rounded-2xl border border-emerald-100 p-6 shadow-sm"
              data-testid="migration-step"
            >
              <div className="w-11 h-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center mb-4">
                <s.icon className="h-5 w-5" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1.5">{s.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>

        {/* Was wird übernommen + Trust */}
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 mb-8">
          {carried.map((c) => (
            <span key={c.label} className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
              <c.icon className="h-4 w-4 text-emerald-600" /> {c.label}
            </span>
          ))}
          <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
            <ShieldCheck className="h-4 w-4 text-emerald-600" /> GoBD-konform
          </span>
          <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
            <RotateCcw className="h-4 w-4 text-emerald-600" /> Rückgängig per Klick
          </span>
        </div>

        {/* Quell-Tools */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-8">
          <span className="text-sm text-gray-500 mr-1">Direkt unterstützt:</span>
          {sources.map((src) => (
            <span
              key={src}
              className="rounded-full border border-gray-200 bg-white px-3 py-1 text-sm text-gray-700"
            >
              {src}
            </span>
          ))}
        </div>

        <div className="text-center">
          <Link to={ctaTo} onClick={() => onCtaClick && onCtaClick()}>
            <Button
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 sm:px-10 py-5 sm:py-7 h-auto text-base sm:text-lg max-w-full whitespace-normal text-center shadow-lg"
              data-testid="migration-switch-cta"
            >
              <ArrowRightLeft className="h-5 w-5 mr-2 shrink-0" />
              {highlightTool ? `Jetzt von ${highlightTool} umziehen` : 'Jetzt umziehen – kostenlos starten'}
            </Button>
          </Link>
          <p className="text-sm text-gray-500 mt-3">
            ✓ Free Plan ohne Kreditkarte &nbsp;•&nbsp; ✓ Vorschau vor dem Import &nbsp;•&nbsp; ✓ Jederzeit zurückrollbar
          </p>
        </div>
      </div>
    </section>
  );
}
